/*
 * SPDX-FileCopyrightText: 2026 The HedgeDoc developers (see AUTHORS file)
 *
 * SPDX-License-Identifier: AGPL-3.0-only
 */
import { useBooleanState } from '../../../hooks/common/use-boolean-state'
import { useTranslatedText } from '../../../hooks/common/use-translated-text'
import { cypressId } from '../../../utils/cypress-attribute'
import { AliasesModal } from '../aliases-modal/aliases-modal'
import { BadgePickerButton } from '../emoji-picker/badge-picker-button'
import { UiIcon } from '../icons/ui-icon'
import { NoteInfoSection } from '../note-info/note-info-section'
import { NotePermissionsSections } from '../note-permissions/note-permissions-sections'
import { useNoteSettingsContext } from '../note-settings/note-settings-context'
import { FolderPathPicker } from './folder-path-picker'
import type { PageSettings } from './page-settings-modal'
import type { ChangeEvent, FormEvent } from 'react'
import React, { Fragment } from 'react'
import { Button, Col, Form, Modal, Row } from 'react-bootstrap'
import { Tags as IconTags } from 'react-bootstrap-icons'
import { Trans } from 'react-i18next'

// The note sections are rendered outside of the form, so that their inputs don't submit the page settings
const PAGE_SETTINGS_FORM_ID = 'page-settings-form'

export interface PageSettingsModalContentProps {
  mode: 'create' | 'edit'
  settings: PageSettings
  submitting: boolean
  pathTooDeep: boolean
  slugPreview: string
  onTitleChange: (event: ChangeEvent<HTMLInputElement>) => void
  onPathChange: (path: string) => void
  onBadgeChange: (badge: string) => void
  onSubmit: (event: FormEvent<HTMLFormElement>) => void
}

interface ContentWithAccessProps extends PageSettingsModalContentProps {
  /** Whether the user may only view the page settings */
  readOnly: boolean
  /** Whether the info and permissions of the note should be shown, needs a surrounding NoteSettingsProvider */
  showNoteSections: boolean
  /** Whether the aliases of the note can be opened, which is only possible for the opened note */
  showAliases: boolean
}

/**
 * Renders the body and the footer of the page settings modal.
 * In edit mode the info and the permissions of the note are shown next to the page fields.
 */
const PageSettingsModalContentWithAccess: React.FC<ContentWithAccessProps> = ({
  mode,
  settings,
  submitting,
  pathTooDeep,
  slugPreview,
  onTitleChange,
  onPathChange,
  onBadgeChange,
  onSubmit,
  readOnly,
  showNoteSections,
  showAliases
}) => {
  const titlePlaceholder = useTranslatedText('pageSettings.title.placeholder')
  const [aliasesVisible, showAliasesModal, hideAliasesModal] = useBooleanState(false)
  const fieldsDisabled = submitting || readOnly

  return (
    <Fragment>
      <Modal.Body>
        <Row className={'gy-3'}>
          <Col xl={showNoteSections ? 6 : 12}>
            {readOnly && (
              <p className={'text-muted small'} {...cypressId('page-settings-read-only-hint')}>
                <Trans i18nKey={'pageSettings.readOnlyHint'} />
              </p>
            )}
            <Form id={PAGE_SETTINGS_FORM_ID} onSubmit={onSubmit}>
              <Form.Group className={'mb-3'}>
                <Form.Label>
                  <Trans i18nKey={'pageSettings.title.label'} />
                </Form.Label>
                <div className={'d-flex gap-2'}>
                  <BadgePickerButton badge={settings.badge} onChange={onBadgeChange} disabled={fieldsDisabled} />
                  <Form.Control
                    type={'text'}
                    value={settings.title}
                    onChange={onTitleChange}
                    placeholder={titlePlaceholder}
                    disabled={mode === 'edit' || fieldsDisabled}
                    maxLength={255}
                    {...cypressId('page-settings-title')}
                  />
                </div>
                {mode === 'edit' && (
                  <Form.Text>
                    <Trans i18nKey={'pageSettings.title.editHint'} />
                  </Form.Text>
                )}
              </Form.Group>
              <Form.Group>
                <Form.Label>
                  <Trans i18nKey={'pageSettings.path.label'} />
                </Form.Label>
                <FolderPathPicker value={settings.path} onChange={onPathChange} disabled={fieldsDisabled} />
                <Form.Text>
                  <Trans i18nKey={'pageSettings.path.help'} />
                </Form.Text>
              </Form.Group>
              <p className={'text-muted small mt-3 mb-0 text-break'}>
                {mode === 'create' ? (
                  <Trans i18nKey={'pageSettings.slugPreview'} values={{ url: slugPreview }} />
                ) : (
                  <Trans i18nKey={'pageSettings.slugChangeHint'} />
                )}
              </p>
            </Form>
            {showAliases && (
              <Fragment>
                <Button
                  variant={'outline-secondary'}
                  size={'sm'}
                  className={'mt-2'}
                  onClick={showAliasesModal}
                  {...cypressId('page-settings-aliases-button')}>
                  <UiIcon icon={IconTags} className={'me-2'} />
                  <Trans i18nKey={'editor.modal.aliases.title'} />
                </Button>
                <AliasesModal show={aliasesVisible} onHide={hideAliasesModal} />
              </Fragment>
            )}
            {showNoteSections && (
              <Fragment>
                <hr />
                <h4>
                  <Trans i18nKey={'editor.noteInfo.title'} />
                </h4>
                <NoteInfoSection />
              </Fragment>
            )}
          </Col>
          {showNoteSections && (
            <Col xl={6}>
              <hr className={'d-xl-none'} />
              <h4>
                <Trans i18nKey={'pageSettings.permissions.heading'} />
              </h4>
              <p className={'text-muted small'}>
                <Trans i18nKey={'pageSettings.permissions.immediateHint'} />
              </p>
              <NotePermissionsSections />
            </Col>
          )}
        </Row>
      </Modal.Body>
      <Modal.Footer>
        <Button
          type={'submit'}
          form={PAGE_SETTINGS_FORM_ID}
          disabled={pathTooDeep || fieldsDisabled}
          {...cypressId('page-settings-submit')}>
          <Trans i18nKey={mode === 'create' ? 'pageSettings.create' : 'pageSettings.save'} />
        </Button>
      </Modal.Footer>
    </Fragment>
  )
}

/**
 * Renders the content of the page settings modal for creating a new page.
 */
export const PageSettingsModalCreateContent: React.FC<PageSettingsModalContentProps> = (props) => {
  return <PageSettingsModalContentWithAccess {...props} readOnly={false} showNoteSections={false} showAliases={false} />
}

/**
 * Renders the content of the page settings modal for an existing note, including its info and permissions.
 * Users who may not edit the note can only view the settings. The aliases can only be managed for the opened note.
 * Needs a surrounding NoteSettingsProvider.
 */
export const PageSettingsModalEditContent: React.FC<PageSettingsModalContentProps> = (props) => {
  const { mayEdit, isOpenNote } = useNoteSettingsContext()
  return (
    <PageSettingsModalContentWithAccess
      {...props}
      readOnly={!mayEdit}
      showNoteSections={true}
      showAliases={isOpenNote}
    />
  )
}
